import queryString from 'query-string'
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import type {
  IDanhMucThuKhac,
  IDanhMucThuKhacPayload,
  LoaiThuKhac,
  TrangThaiDanhMucThuKhac,
} from '@/interfaces/danhMucThuKhacInterfaces'

const PREFIX = '/danh-muc-thu-khac'

/** Map mã uppercase của backend ↔ camelCase của FE. */
const TRANG_THAI_TO_FE: Record<string, TrangThaiDanhMucThuKhac> = {
  DANG_HOAT_DONG: 'dangHoatDong',
  TAM_NGUNG: 'ngungHoatDong',
}
const TRANG_THAI_TO_BE: Record<TrangThaiDanhMucThuKhac, string> = {
  dangHoatDong: 'DANG_HOAT_DONG',
  ngungHoatDong: 'TAM_NGUNG',
}

function mapDanhMucThuKhacFromApi(raw: Record<string, unknown>): IDanhMucThuKhac {
  const loai = raw.loaiThuKhac
  const trangThai = raw.trangThai
  return {
    id: String(raw.id ?? ''),
    maThuKhac: String(raw.maThuKhac ?? ''),
    tenThuKhac: String(raw.tenThuKhac ?? ''),
    loaiThuKhac:
      loai === 'CO_DINH' || loai === 'PHAN_TRAM' || loai === 'TUY_CHON' ? loai : 'TUY_CHON',
    giaTri: raw.giaTri != null ? Number(raw.giaTri) : null,
    phanTram: raw.phanTram != null ? Number(raw.phanTram) : null,
    moTa: raw.moTa != null ? String(raw.moTa) : null,
    trangThai: TRANG_THAI_TO_FE[typeof trangThai === 'string' ? trangThai : ''] ?? 'dangHoatDong',
    sapXep: Number.isFinite(Number(raw.sapXep)) ? Number(raw.sapXep) : 0,
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

function toPayload(body: IDanhMucThuKhacPayload) {
  const payload: Record<string, unknown> = {
    tenThuKhac: body.tenThuKhac.trim(),
    loaiThuKhac: body.loaiThuKhac,
  }
  const ma = body.maThuKhac?.trim()
  if (ma) payload.maThuKhac = ma
  if (body.moTa?.trim()) payload.moTa = body.moTa.trim()
  if (body.loaiThuKhac === 'CO_DINH' && body.giaTri != null) payload.giaTri = body.giaTri
  if (body.loaiThuKhac === 'PHAN_TRAM' && body.phanTram != null) payload.phanTram = body.phanTram
  return payload
}

export function danhMucThuKhacListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  for (const key of ['maThuKhac', 'tenThuKhac'] as const) {
    const v = q[key]
    if (typeof v === 'string' && v.trim()) out[key] = v.trim()
  }
  const loaiThuKhac = q.loaiThuKhac
  if (loaiThuKhac === 'CO_DINH' || loaiThuKhac === 'PHAN_TRAM' || loaiThuKhac === 'TUY_CHON') {
    out.loaiThuKhac = loaiThuKhac
  }
  // trangThai: FE dùng camelCase, BE dùng uppercase
  const trangThaiFe = q.trangThai
  if (typeof trangThaiFe === 'string' && TRANG_THAI_TO_BE[trangThaiFe as TrangThaiDanhMucThuKhac]) {
    out.trangThai = TRANG_THAI_TO_BE[trangThaiFe as TrangThaiDanhMucThuKhac]
  }
  return out
}

export function fetchDanhSachDanhMucThuKhac(search: string): Promise<PagedListResult<IDanhMucThuKhac>> {
  const params = danhMucThuKhacListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-danh-muc-thu-khac`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: (data as Record<string, unknown>[]).map(mapDanhMucThuKhacFromApi),
        paging,
      }
    }),
  )
}

export function fetchChiTietDanhMucThuKhac(id: string | number): Promise<IDanhMucThuKhac> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-danh-muc-thu-khac`).then((res: unknown) =>
      mapDanhMucThuKhacFromApi(normalizeDetailResponse(res) as Record<string, unknown>),
    ),
  )
}

export function taoDanhMucThuKhac(body: IDanhMucThuKhacPayload) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-danh-muc-thu-khac`, toPayload(body)))
}

export function capNhatDanhMucThuKhac(id: string | number, body: IDanhMucThuKhacPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-danh-muc-thu-khac`, toPayload(body)))
}

export function xoaDanhMucThuKhac(id: string | number) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-danh-muc-thu-khac`))
}

export type XoaNhieuDanhMucThuKhacResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều danh mục thu khác — gọi API xóa từng id (loại trùng id). */
export async function xoaNhieuDanhMucThuKhac(ids: string[]): Promise<XoaNhieuDanhMucThuKhacResult> {
  const uniqueIds = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)))
  const failed: XoaNhieuDanhMucThuKhacResult['failed'] = []
  let ok = 0
  for (const id of uniqueIds) {
    try {
      await xoaDanhMucThuKhac(id)
      ok += 1
    } catch (e) {
      failed.push({
        id,
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function sapXepDanhMucThuKhac(danhSach: Array<{ id: string; sapXep: number }>) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/sap-xep-danh-muc-thu-khac`, { danhSach }))
}

export function chuyenTrangThaiDanhMucThuKhac(id: string | number, trangThai: TrangThaiDanhMucThuKhac) {
  const ma = TRANG_THAI_TO_BE[trangThai]
  return apiRequest(
    ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-danh-muc-thu-khac`, { trangThai: ma }),
  )
}

export type { LoaiThuKhac, TrangThaiDanhMucThuKhac }
